import { useEffect, useRef } from 'react';
import { useDemo } from '../context/DemoContext';

export const useDemoOrchestrator = () => {
  const { 
    state, 
    addMessage, 
    addInterAgentMessage, 
    setTypingStatus,
    dispatch 
  } = useDemo();
  
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const stepRef = useRef(0);
  const startTimeRef = useRef<Date | null>(null);

  const getSpeedDelay = () => {
    switch (state.speed) {
      case 'slow': return 3000;
      case 'fast': return 800;
      default: return 1500;
    }
  };

  // Generate progressive timestamps starting from current time
  const getTimestamp = (minuteOffset: number = 0, secondOffset: number = 0) => {
    if (!startTimeRef.current) {
      startTimeRef.current = new Date();
    }
    
    const timestamp = new Date(startTimeRef.current);
    timestamp.setMinutes(timestamp.getMinutes() + minuteOffset);
    timestamp.setSeconds(timestamp.getSeconds() + secondOffset);
    
    return timestamp.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  // Robot processing animation helper
  const setProcessingStatus = async (agentName: string, verb: string, duration: number) => {
    setTypingStatus(`${agentName} is ${verb}`, true);
    await sleep(duration);
    setTypingStatus(`${agentName} is ${verb}`, false);
  };

  const executeStep = async (step: number) => {
    const delay = getSpeedDelay();

    switch (step) {
      case 1:
        // Sarah Wilson initial complaint
        await sleep(delay / 2);
        
        setTypingStatus("Sarah Wilson", true);
        await sleep(delay * 1.5);
        setTypingStatus("Sarah Wilson", false);
        
        addMessage({
          content: "Hello. I placed order #R-55821 three days ago and it was supposed to arrive yesterday. My daughter's birthday is tomorrow and the gift hasn't arrived yet.",
          sender: "Sarah Wilson", 
          senderType: "customer"
        });
        
        await sleep(delay * 0.8);
        
        setTypingStatus("Sarah Wilson", true);
        await sleep(delay * 1.2);
        setTypingStatus("Sarah Wilson", false);
        
        addMessage({
          content: "I'm really upset and worried I won't have anything to give her.",
          sender: "Sarah Wilson",
          senderType: "customer"
        });
        break;

      case 2:
        // Julia processes emotions and responds
        
        // Processing animation
        await setProcessingStatus("Julia", "detecting emotions", delay * 1.6);
        
        // Julia's grouped activities
        addInterAgentMessage({
          from: 'Julia',
          to: 'system',
          content: `🤖 Julia (Virtual Support)
⎿ 🧠 Feels: Customer is very stressed (frustration=0.86 • urgency=0.91 • sadness=0.47)
    💭 Thinks: Show empathy first, then investigate.
    ⚡ Does: Escalate case to Ops & Manager with VIP context.`,
          messageType: 'activity-group'
        });
        
        await sleep(delay * 0.8);
        
        // Message to customer
        await setProcessingStatus("Julia", "analyzing emotions", delay * 0.6);
        
        addMessage({
          content: "I'm truly sorry to hear this, Sarah. I understand how important this is for you and your daughter.",
          sender: "Julia",
          senderType: "agent",
          agentRole: "support"
        });
        
        await sleep(delay * 0.4);
        
        addMessage({
          content: "I'll check the order status right away and make sure we fix this in time.",
          sender: "Julia",
          senderType: "agent", 
          agentRole: "support"
        });
        
        // Julia greeting message
        await sleep(delay * 0.3);
        
        addMessage({
          content: "Hello Sarah, thank you for reaching out.",
          sender: "Julia",
          senderType: "agent",
          agentRole: "support"
        });
        
        await sleep(delay * 0.3);
        
        // Internal message with clear recipient
        addInterAgentMessage({
          from: 'Julia',
          to: 'operations',
          content: "Internal message to @Oliver @Alex:\nVIP customer order #R-55821 — likely delivery failure. Please investigate anomaly and advise policy. Ticket #TCK-8823 opened.",
          messageType: 'escalation'
        });
        break;

      case 3:
        // Oliver analyzes systems AND recommends solution (CONSOLIDATED)
        
        // Processing animation
        await setProcessingStatus("Oliver", "analyzing systems", delay * 1.8);
        
        addInterAgentMessage({
          from: 'Oliver',
          to: 'system',
          content: `🤖 Oliver (Virtual Ops)
⎿ 💭 Thinks: Compare internal system with courier data.
    ⚡ Does: Query internal order DB
    ⚡ Does: Query courier API
    📊 Reports: Internal says "Delivered 17:42", courier says "In transit — stuck at hub".
    💭 Thinks: Courier unreliable + VIP + birthday tomorrow → best solution = replacement + voucher.
    ⚡ Does: Recommend express replacement
    ⚡ Does: Recommend €50 voucher`,
          messageType: 'activity-group'
        });
        
        await sleep(delay * 1.5);
        
        // Internal message to Emma
        addInterAgentMessage({
          from: 'Oliver',
          to: 'warehouse',
          content: "Internal message to @Emma (Warehouse):\nMismatch detected. Can you confirm stock for replacement?",
          messageType: 'escalation'
        });
        
        await sleep(delay * 0.5);
        
        // Internal message to Alex
        addInterAgentMessage({
          from: 'Oliver',
          to: 'manager',
          content: "Internal message to @Alex (Virtual Manager):\nBased on @Emma's confirmation, I recommend express replacement + €50 voucher. Needs approval.",
          messageType: 'recommendation'
        });
        break;

      case 4:
        // Emma responds (human with typing)
        
        setTypingStatus("Emma Johnson", true);
        await sleep(delay * 2); // Humans type slower
        setTypingStatus("Emma Johnson", false);
        
        addInterAgentMessage({
          from: 'Emma',
          to: 'team',
          content: "Yes, the products are in stock.",
          messageType: 'internal'
        });
        
        await sleep(delay * 0.5);
        
        setTypingStatus("Emma Johnson", true);
        await sleep(delay * 1.2);
        setTypingStatus("Emma Johnson", false);
        
        addInterAgentMessage({
          from: 'Emma',
          to: 'team',
          content: "I can prepare a replacement package within 20 minutes.",
          messageType: 'internal'
        });
        break;

      case 5:
        // Alex makes strategic decision AND executes (CONSOLIDATED)
        
        // Processing animation
        await setProcessingStatus("Alex", "evaluating strategy", delay * 1.4);
        
        addInterAgentMessage({
          from: 'Alex',
          to: 'system',
          content: `🤖 Alex (Virtual Manager)
⎿ 💭 Thinks: VIP + missed delivery + urgent occasion → high churn risk; goodwill protects loyalty.
    ⚡ Does: Approve replacement + voucher for this case
    ⚡ Does: Draft new policy proposal`,
          messageType: 'activity-group'
        });
        
        await sleep(delay * 0.8);
        
        // Internal message to team
        addInterAgentMessage({
          from: 'Alex',
          to: 'team',
          content: "@team (Ops + Support): Approved for this case. Proposing new policy draft: VIP + failed delivery = auto replacement + personalized voucher.",
          messageType: 'internal'
        });
        break;

      case 6:
        // Oliver executes solution
        
        // Processing animation
        await setProcessingStatus("Oliver", "creating orders", delay * 1.2);
        
        addInterAgentMessage({
          from: 'Oliver',
          to: 'system',
          content: `🤖 Oliver (Virtual Ops)
⎿ ⚡ Does: Create replacement shipment (express courier)
    ⚡ Does: Apply €50 voucher to customer account`,
          messageType: 'activity-group'
        });
        
        await sleep(delay * 0.7);
        
        // Processing
        await setProcessingStatus("Oliver", "processing order", delay * 0.7);
        
        // Internal message
        addInterAgentMessage({
          from: 'Oliver',
          to: 'team',
          content: "Internal message to @team (Ops + Support):\nReplacement created. Courier pickup in 45 minutes. Voucher applied.",
          messageType: 'update'
        });
        break;

      case 7:
        // Julia delivers solution to customer
        
        // Processing animation
        await setProcessingStatus("Julia", "personalizing response", delay * 1.6);
        
        addInterAgentMessage({
          from: 'Julia',
          to: 'system',
          content: `🤖 Julia (Virtual Support)
⎿ 🧠 Feels: Customer's frustration has eased (frustration=0.32 • trust=0.81)
    💭 Thinks: Reassure with ETA + add personalization.
    ⚡ Does: Check customer profile (frequent Home & Lifestyle purchases)`,
          messageType: 'activity-group'
        });
        
        await sleep(delay * 1.0);
        
        // Processing
        await setProcessingStatus("Julia", "personalizing response", delay * 0.8);
        
        addMessage({
          content: "Good news, Sarah. We've sent a replacement via express courier. It will arrive tomorrow morning, well before the birthday.",
          sender: "Julia",
          senderType: "agent",
          agentRole: "support"
        });
        
        await sleep(delay * 0.4);
        
        addMessage({
          content: "I've also added a €50 voucher tailored for our Home & Lifestyle collection, which you've enjoyed before.",
          sender: "Julia", 
          senderType: "agent",
          agentRole: "support"
        });
        break;

      case 8:
        // Sarah responds positively
        
        setTypingStatus("Sarah Wilson", true);
        await sleep(delay * 1.2);
        setTypingStatus("Sarah Wilson", false);
        
        addMessage({
          content: "Thank you so much! You fixed the problem.",
          sender: "Sarah Wilson",
          senderType: "customer"
        });
        
        await sleep(delay * 0.5);
        
        setTypingStatus("Sarah Wilson", true);
        await sleep(delay * 1.0);
        setTypingStatus("Sarah Wilson", false);
        
        addMessage({
          content: "You even remembered what I like. That means a lot.",
          sender: "Sarah Wilson",
          senderType: "customer"
        });
        break;

      case 9:
        // Alex logs policy proposal
        
        // Processing animation
        await setProcessingStatus("Alex", "drafting policies", delay * 1.8);
        
        addInterAgentMessage({
          from: 'Alex',
          to: 'system',
          content: `🤖 Alex (Virtual Manager)
⎿ ⚡ Does: Log policy proposal
    💭 Thinks: Predicted impact — FCR +8%, NPS +14, Churn −20%`,
          messageType: 'activity-group'
        });
        
        await sleep(delay * 0.6);
        
        // Processing
        await setProcessingStatus("Alex", "drafting policies", delay * 1.8);
        
        // Internal escalation to Winston
        addInterAgentMessage({
          from: 'Alex',
          to: 'director',
          content: "Internal message to @Winston (Operations Director):\nMr. Smith, we request your approval for this rule. Ticket #TCK-8823 attached.",
          messageType: 'escalation'
        });
        break;

      case 10:
        // Winston Smith approves (human)
        
        setTypingStatus("Winston Smith", true);
        await sleep(delay * 2.5);
        setTypingStatus("Winston Smith", false);
        
        addInterAgentMessage({
          from: 'Winston',
          to: 'team',
          content: "Approved. Roll out this rule across ABC Global Retail. Monitor KPIs in next review cycle — I want to see impact across all regions.",
          messageType: 'internal'
        });
        break;

      case 11:
        // Oliver implements policy in systems
        
        // Processing animation
        await setProcessingStatus("Oliver", "implementing policy", delay * 1.2);
        
        addInterAgentMessage({
          from: 'Oliver',
          to: 'system',
          content: `🤖 Oliver (Virtual Ops)
⎿ ⚡ Does: Implement approved rule in policy system
    ⚡ Does: Distribute to Support and Ops
    📊 Reports: Policy active — future mismatches will auto-trigger express replacement + personalized voucher.
    📊 Reports: KPI forecast — First-Contact Resolution +8% • Avg Handling Time −30s • NPS +14`,
          messageType: 'activity-group'
        });
        
        await sleep(delay * 0.6);
        
        // Internal message
        addInterAgentMessage({
          from: 'Oliver',
          to: 'team',
          content: 'Internal message to @team (Ops + Support):\nApproved rule now active: VIP + failed delivery = replacement + personalized voucher.',
          messageType: 'update'
        });
        break;

      case 12:
        // Virtual Employees learn - GROUPED
        
        // Processing animation
        await setProcessingStatus("Julia", "learning patterns", delay * 1.0);
        
        addInterAgentMessage({
          from: 'Julia',
          to: 'system',
          content: `🤖 Julia (Virtual Support)
⎿ 💡 Learns: Providing a clear ETA reduces frustration — I'll keep using this tactic.
    💡 Learns: Apply the new VIP policy directly next time, without escalation, to delight customers faster.
    💡 Learns: Personalized vouchers based on purchase history increase customer delight.`,
          messageType: 'activity-group'
        });
        
        await sleep(delay * 0.5);
        
        // Processing animation
        await setProcessingStatus("Oliver", "updating workflows", delay * 0.8);
        
        break;

      case 13:
        // PLACEHOLDER - Content to be replaced
        await sleep(delay);
        break;

      case 14:
        // REMOVED - redundant policy execution block
        break;

      case 15:
        // Virtual Employees learning phase
        await sleep(delay * 0.5);
        
        await sleep(delay * 0.5);
        
        // Processing animation
        await setProcessingStatus("Oliver", "updating workflows", delay * 0.8);
        
        addInterAgentMessage({
          from: 'Oliver',
          to: 'system',
          content: `🤖 Oliver (Virtual Ops)
⎿ 💡 Learns: Bind anomaly pattern to automatic replacement workflow.
    💡 Learns: Policy automation reduces need for manual escalation — efficiency gain confirmed.`,
          messageType: 'activity-group'
        });
        
        await sleep(delay * 0.5);
        
        // Processing animation  
        await setProcessingStatus("Alex", "strategic review", delay * 0.8);
        
        addInterAgentMessage({
          from: 'Alex',
          to: 'system',
          content: `🤖 Alex (Virtual Manager)
⎿ 💡 Learns: Strategic approval at the right time ensures balance between customer satisfaction and cost control.`,
          messageType: 'activity-group'
        });
        break;

      case 16:
        // Final Conclusion Boxed Summary
        console.log('DEBUG: Case 16 - Conclusion executing');
        await sleep(delay);
        
        addInterAgentMessage({
          from: 'system',
          to: 'all',
          content: `🔑 **Key Takeaways for Investors**

What this demo illustrates:
• 🧠 Emotion-driven support → faster resolutions  
• ⚡ Autonomous ops → efficiency at scale  
• 💡 Continuous learning → compounding improvements  

Investor Impact:
• Expands across industries (retail, logistics, finance, healthcare)  
• Built-in learning loop — performance improves with every case  
• Cost-efficient — reduces need for human escalation  
• Proprietary tech moat — beyond standard GPT models  

🚀 With your investment, Aisberg will scale this prototype globally and define the category of digital employees.

**Contact:** investors@aisberg.ai`,
          messageType: 'conclusion'
        });
        
        console.log('DEBUG: Conclusion message added with messageType: conclusion');
        
        // Complete demo
        await sleep(delay * 2);
        dispatch({ type: 'COMPLETE_DEMO' });
        break;

      default:
        dispatch({ type: 'COMPLETE_DEMO' });
        break;
    }

    let nextStep = step + 1;
    
    if (nextStep <= 16 && state.isAutoPlaying) {
      stepRef.current = nextStep;
      dispatch({ type: 'NEXT_STEP' });
    }
  };

  const sleep = (ms: number): Promise<void> => {
    return new Promise(resolve => setTimeout(resolve, ms));
  };

  useEffect(() => {
    if (state.isAutoPlaying && !state.isComplete) {
      timeoutRef.current = setTimeout(() => {
        executeStep(stepRef.current + 1);
      }, 500);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [state.isAutoPlaying, state.currentStep, state.speed]);

  useEffect(() => {
    stepRef.current = state.currentStep;
  }, [state.currentStep]);

  // Reset step counter and timestamps when demo is reset
  useEffect(() => {
    if (state.currentStep === 0) {
      stepRef.current = 0;
      startTimeRef.current = null; // Reset timestamps for fresh start
    }
  }, [state.currentStep]);
};

export default useDemoOrchestrator;